import React, { useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import P from 'polyline-encoded'
import { useEffect } from 'react'
import pickupIconSVG from 'src/assets/images/icon_map_pickup.svg'
import taxiIconSVG from 'src/assets/images/icon_map_taxi.svg'
import destinationIconSVG from 'src/assets/images/icon_map_destination.svg'

interface ISize {
  width: string,
  height: string
}

interface IProps {
  size?: ISize
  geoJson?: any
  maxZoom?: number
  orderId?: number
  polygons?: Array<any>
  minZoom?: number
  polyLine?: string
  pickUpMarker?: any
  destinationMarker?: any
  taxiMarker?: any
}


const Map = React.memo(function Map(props: IProps) {
  const map = useRef(null)
  const taxiMarker = useRef(null);
  const destinationMarker = useRef(null);
  const pickUpMarker = useRef(null);
  const geoDestinationMarker = useRef(null);
  const geoPickupMarker = useRef(null);
  const polyline = useRef(null)
  const feature = useRef(null);
  const pickupIcon = L.icon({ iconUrl: pickupIconSVG, iconSize: [32, 32], });
  const destinationIcon = L.icon({ iconUrl: destinationIconSVG, iconSize: [32, 32] });
  const taxiIcon = L.icon({ iconUrl: taxiIconSVG, iconSize: [32, 32], });
  const [shouldZoom, setShouldZoom] = useState(true)
  const markers = useMemo(() => L.featureGroup(), [])
  const layer = useMemo(() => L.featureGroup(), [])

  useEffect(() => {
    document.getElementById('mapContainer').innerHTML = "<div id='map' style='width: 100%; height: 300px;'></div>";
    map.current = L.map('map', { center: [56.2639, 9.5018], zoom: 10 })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map.current)

    map.current.on("dragstart", function () {
      setShouldZoom(false)
    });
  }, [])

  useEffect(() => {
    setShouldZoom(true)
  }, [props.orderId])

  useEffect(() => {
    if(feature.current){
      if (map.current.hasLayer(feature.current)) {
        map.current.removeLayer(feature.current)
      }
    }
    if(geoDestinationMarker.current){
      if (markers.hasLayer(geoDestinationMarker.current)) {
        markers.removeLayer(geoDestinationMarker.current)
      }
    }
    if(geoPickupMarker.current){
      if (markers.hasLayer(geoPickupMarker.current)) {
        markers.removeLayer(geoPickupMarker.current)
      }
    }
   
    if (props.geoJson) {
      const destination = props.geoJson.features[0].geometry.coordinates[props.geoJson.features[0].geometry.coordinates.length - 1]
      const pickup = props.geoJson.features[0].geometry.coordinates[0]
      geoDestinationMarker.current = L.marker([destination[1], destination[0]], { icon: destinationIcon }).addTo(markers)
      geoPickupMarker.current = L.marker([pickup[1], pickup[0]], { icon: pickupIcon }).addTo(markers)
      markers.addTo(map.current)
      feature.current = L.geoJSON(props.geoJson).addTo(map.current)
      map.current.fitBounds(feature.current.getBounds())
    } else if (feature.current && map.current.hasLayer(feature.current)) {
      map.current.removeLayer(feature)
    }
  }, [props.geoJson, markers, destinationIcon, pickupIcon])

  useEffect(() => {
    if (props.polyLine) {
      const coordinates = P.decode(props.polyLine);
      polyline.current = L.polyline(coordinates)
      geoDestinationMarker.current = L.marker(coordinates[coordinates.length - 1], { icon: destinationIcon }).addTo(markers)
      geoPickupMarker.current = L.marker(coordinates[0], { icon: pickupIcon }).addTo(markers)
      markers.addTo(map.current)
      feature.current = polyline.current.addTo(map.current)
      map.current.fitBounds(feature.current.getBounds())
    }
  }, [destinationIcon, markers, pickupIcon, props.polyLine])


  useEffect(() => {
    if (props.polygons && props.polygons.length) {
      layer.clearLayers()
      props.polygons.forEach(polygon => {
        const p = L.geoJSON(polygon.geometry, {
          style: {
            color: '#fff',
            fillColor: '#2bab66',
            fillOpacity: 0.7,
            weight: 0.7

          }
        }).addTo(layer)
        p.bindPopup(polygon.navn)
      })
      layer.addTo(map.current)
      map.current.fitBounds(layer.getBounds())
    }
  }, [markers, props.polygons])


  useEffect(() => {
    if(pickUpMarker.current){
      if (markers.hasLayer(pickUpMarker.current)) {
        markers.removeLayer(pickUpMarker.current)
      }
    }

    if(destinationMarker.current){
      if (markers.hasLayer(destinationMarker.current)) {
        markers.removeLayer(destinationMarker.current)
      }
    }
    if(taxiMarker.current){
      if (markers.hasLayer(taxiMarker.current)) {
        markers.removeLayer(taxiMarker.current)
      }
    }

    if (props.pickUpMarker) {
      pickUpMarker.current = L.marker([props.pickUpMarker.latitude, props.pickUpMarker.longitude], { icon: pickupIcon })
      pickUpMarker.current.addTo(markers)
    }
    if (props.destinationMarker) {
      destinationMarker.current = L.marker([props.destinationMarker.latitude, props.destinationMarker.longitude], { icon: destinationIcon })
      destinationMarker.current.addTo(markers)
    }

    if (props.taxiMarker) {
      taxiMarker.current = L.marker([props.taxiMarker.latitude, props.taxiMarker.longitude], { icon: taxiIcon })
      taxiMarker.current.addTo(markers)
    }

    if (props.pickUpMarker || props.taxiMarker || props.pickUpMarker) {
      markers.addTo(map.current)
      if (shouldZoom) {
        map.current.flyToBounds(markers.getBounds(), { 'duration': 0.300 })
      }
    }
  }, [props.pickUpMarker, props.destinationMarker, props.taxiMarker, pickupIcon, destinationIcon, taxiIcon, markers, shouldZoom])

  return <div id='mapContainer'>
    <div id="map" style={{ width: '100%', height: '300px' }}></div>
  </div>
})

export default Map